// C14 transport is a binding check, never an issuer of application authority.
import { APP_BRIDGE } from './app-limits.mjs';

export const RUNTIME_PROTOCOL = 'poneva.runtime.v2';
// Message sizes come from the App limits registry, the one owner of App limits.
export const RUNTIME_LIMITS = Object.freeze({
  bytes: APP_BRIDGE.bytes,
  depth: APP_BRIDGE.depth,
  nodes: APP_BRIDGE.nodes,
  pending: 20,
  timeoutMs: 30000,
  messagesPerSecond: 100,
});
export const BOOTSTRAP_TTL_MS = 60000;
export const READ_LEASE_TTL_MS = 300000;
export const FRAME_SANDBOX = 'allow-scripts allow-same-origin';
const identifier = /^[A-Za-z0-9_-]{1,128}$/;
const digest = /^[a-f0-9]{64}$/;
const operation = /^[a-z][a-z0-9-]{0,31}\.[a-z][a-zA-Z0-9.-]{0,95}$/;
const kinds = new Set([
  'request',
  'reply',
  'subscribe',
  'event',
  'unsubscribe',
  'cancel',
  'invalidate',
]);

export function exactOrigin(value) {
  if (typeof value !== 'string') throw new TypeError('invalid_origin');
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.origin !== value || url.username || url.password)
    throw new TypeError('invalid_origin');
  return value;
}

export function documentScope(input) {
  if (!input || typeof input !== 'object') throw new TypeError('invalid_scope');
  const result = {};
  for (const key of ['documentNonce', 'artifactId', 'revisionId']) {
    if (typeof input[key] !== 'string' || !identifier.test(input[key]))
      throw new TypeError('invalid_scope');
    result[key] = input[key];
  }
  for (const key of ['buildDigest', 'assetManifestDigest']) {
    if (typeof input[key] !== 'string' || !digest.test(input[key]))
      throw new TypeError('invalid_scope');
    result[key] = input[key];
  }
  result.appOrigin = exactOrigin(input.appOrigin);
  result.hostOrigin = exactOrigin(input.hostOrigin);
  if (result.appOrigin === result.hostOrigin) throw new TypeError('isolated_origin_required');
  return Object.freeze(result);
}

export function authorityBinding(input) {
  const scope = documentScope(input);
  for (const key of ['companyId', 'actorId'])
    if (typeof input[key] !== 'string' || !identifier.test(input[key]))
      throw new TypeError('invalid_authority');
  if (
    typeof input.versionId !== 'string' ||
    !identifier.test(input.versionId) ||
    !['viewer', 'editor'].includes(input.readPurpose)
  )
    throw new TypeError('invalid_read_binding');
  return Object.freeze({
    ...scope,
    companyId: input.companyId,
    actorId: input.actorId,
    versionId: input.versionId,
    readPurpose: input.readPurpose,
  });
}

export function validOperation(value) {
  return typeof value === 'string' && operation.test(value);
}

function primitive(value) {
  return (
    value === null ||
    ['boolean', 'string'].includes(typeof value) ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}
function propertyKeys(value, budget) {
  if (!Array.isArray(value)) return Object.keys(value);
  if (value.length > RUNTIME_LIMITS.nodes - budget.nodes) throw new TypeError('payload_limit');
  const keys = Reflect.ownKeys(value);
  if (
    keys.length !== value.length + 1 ||
    keys.some((key, index) => key !== (index === value.length ? 'length' : String(index)))
  )
    throw new TypeError('invalid_payload');
  return keys.slice(0, -1);
}
function walkProperties(value, depth, budget) {
  if (!Array.isArray(value) && ![Object.prototype, null].includes(Object.getPrototypeOf(value)))
    throw new TypeError('invalid_payload');
  budget.seen.add(value);
  for (const key of propertyKeys(value, budget)) {
    if (
      ['__proto__', 'constructor', 'prototype'].includes(key) ||
      key.length > RUNTIME_LIMITS.bytes
    )
      throw new TypeError('invalid_payload');
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !('value' in descriptor)) throw new TypeError('invalid_payload');
    walkJson(descriptor.value, depth + 1, budget);
  }
  budget.seen.delete(value);
}
function walkJson(value, depth, budget) {
  if (++budget.nodes > RUNTIME_LIMITS.nodes || depth > RUNTIME_LIMITS.depth)
    throw new TypeError('payload_limit');
  if (typeof value === 'string' && value.length > RUNTIME_LIMITS.bytes)
    throw new TypeError('payload_limit');
  if (primitive(value)) return;
  if (!value || typeof value !== 'object' || budget.seen.has(value))
    throw new TypeError('invalid_payload');
  walkProperties(value, depth, budget);
}

export function boundedJson(value) {
  walkJson(value, 0, { nodes: 0, seen: new Set() });
  const encoded = JSON.stringify(value);
  if (new TextEncoder().encode(encoded).length > RUNTIME_LIMITS.bytes)
    throw new TypeError('payload_limit');
  return JSON.parse(encoded);
}

export function envelope(scope, type, requestId, operationName, payload) {
  if (
    !kinds.has(type) ||
    typeof requestId !== 'string' ||
    !identifier.test(requestId) ||
    !validOperation(operationName)
  )
    throw new TypeError('invalid_envelope');
  return {
    protocol: RUNTIME_PROTOCOL,
    ...documentScope(scope),
    type,
    requestId,
    operation: operationName,
    payload: boundedJson(payload),
  };
}

export function acceptsEnvelope(value, scope) {
  try {
    const message = boundedJson(value);
    if (
      message.protocol !== RUNTIME_PROTOCOL ||
      !kinds.has(message.type) ||
      typeof message.requestId !== 'string' ||
      !identifier.test(message.requestId) ||
      !validOperation(message.operation)
    )
      return false;
    return Object.keys(documentScope(scope)).every((key) => message[key] === scope[key]);
  } catch {
    return false;
  }
}
